/**
 * Куда выводят «Назад» и «Завершить урок» (02.10.2026, свайп «назад» к сборке 1.1.1).
 *
 * ⚠️ ЗАЧЕМ. Кнопки делали `navigate('/app/lessons')` — шаг ВПЕРЁД. Урок, открытый со страницы курса, с главной,
 * из ленты или пуша, выводил всегда на список уроков, а в истории оставалась запись урока: «назад» со списка
 * (жест на iPhone, кнопка на Android) возвращал в только что закрытый урок.
 *
 * ⚠️ ПОЧЕМУ НЕ ПРОСТО «ШАГ НАЗАД». Уроков в истории может быть несколько подряд: пуш или строка ленты, нажатые из
 * открытого урока, кладут второй урок поверх первого. Шаг назад из второго вернул бы в первый — а «Завершить» и
 * «Назад» обязаны ВЫВОДИТЬ ИЗ УРОКА (решение владельца 02.10.2026). Поэтому запоминается последний экран ВНЕ
 * урока, и выход возвращает сразу на него — через все уроки цепочки.
 *
 * Экрана вне урока в истории нет (приложение открыли пушем прямо в урок, страницу обновили) — выход ведёт на
 * список уроков заменой адреса: урок из истории пропадает, «назад» со списка в него не вернёт.
 */

/** Адрес плеера урока ученика: `/app/lessons/<id>`. Страница курса `/app/lessons/marathon/<id>` — не урок. */
export function isLessonPath(pathname: string): boolean {
  const m = pathname.replace(/\/+$/, '').match(/^\/app\/lessons\/([^/]+)$/);
  return !!m && m[1] !== 'marathon';
}

/**
 * На сколько записей истории шагнуть назад, чтобы выйти из урока; `null` — шагать некуда, вести на список.
 * `outsideIdx` — номер записи последнего экрана вне урока, `currentIdx` — номер записи открытого урока.
 * Запись «вне урока» годится, только если она РАНЬШЕ урока: ушли с неё вперёд — и вернулись назад в урок
 * (номер больше текущего) — значит, она уже не позади.
 */
export function lessonExitDelta(currentIdx: number | null, outsideIdx: number | null): number | null {
  if (currentIdx === null || outsideIdx === null) return null;
  if (outsideIdx >= currentIdx) return null;
  return outsideIdx - currentIdx;
}

let lastOutsideIdx: number | null = null;

function historyIdx(): number | null {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' ? idx : null;
}

/** Запомнить экран: вызывается на каждую смену адреса. Чистая часть — `noteLocation`. */
export function noteLocation(pathname: string, idx: number | null): void {
  if (isLessonPath(pathname)) return;
  lastOutsideIdx = idx;
}

type RouterLike = {
  state: { location: { pathname: string } };
  subscribe: (fn: (state: { location: { pathname: string } }) => void) => unknown;
};

/** Подписка на роутер — один раз при запуске (`main.tsx`). Работает и на сайте, и в приложении. */
export function initLessonExit(router: RouterLike): void {
  noteLocation(router.state.location.pathname, historyIdx());
  router.subscribe((state) => noteLocation(state.location.pathname, historyIdx()));
}

/** Шаг выхода из урока для открытого сейчас экрана. */
export function currentLessonExit(): number | null {
  return lessonExitDelta(historyIdx(), lastOutsideIdx);
}

/** Только для проверок. */
export function __resetLessonExit(): void {
  lastOutsideIdx = null;
}

/** Совместимо с `useNavigate()` и с `router.navigate`: у обоих переход по адресу и шаг по истории — разные вызовы. */
type Navigate = {
  (to: string, opts?: { replace?: boolean }): unknown;
  (delta: number): unknown;
};

/**
 * Выйти из урока: назад до экрана, с которого вошли в урок; нет такого — на `fallback` заменой адреса.
 * Перехват несохранённого ответа (`useLeaveWhileRecording`) срабатывает в обоих случаях — переход идёт через роутер.
 */
export function leaveLesson(navigate: Navigate, fallback: string): void {
  const delta = currentLessonExit();
  if (delta !== null) navigate(delta);
  else navigate(fallback, { replace: true });
}
